import type { Event } from './events';
import type { Coordinate, Drawing, Patient, Scenario, Unit } from './scenario';
import type { Organisation } from './signs';
import type { TriageCategory } from './triage';

export type PlacedSign = {
  id: string;
  signType: string;
  position: Coordinate;
  label?: string;
  organisation?: Organisation;
};

export type PatientState = Patient & {
  category?: TriageCategory;
  destination?: string;
  surgicalIndication?: boolean;
  transportReleased?: boolean;
};

export type Reinforcement = {
  id: string;
  signType: string;
  count: number;
  text?: string;
  arrived: boolean;
};

export type RunState = {
  signs: Record<string, PlacedSign>;
  units: Record<string, Unit>;
  patients: Record<string, PatientState>;
  reinforcements: Record<string, Reinforcement>;
  triggeredInjects: string[];
  drawings: Record<string, Drawing>;
  notes: { id: string; time: number; text: string }[];
};

const byId = <T extends { id: string }>(items: T[]): Record<string, T> =>
  Object.fromEntries(items.map((i) => [i.id, i]));

export function initialState(scenario: Scenario): RunState {
  const signs: Record<string, PlacedSign> = {};
  for (const u of scenario.units) {
    if (u.position)
      signs[u.id] = {
        id: u.id,
        signType: u.type,
        position: u.position,
        label: u.callsign,
        organisation: u.organisation,
      };
  }
  return {
    signs,
    units: byId(scenario.units),
    patients: byId(scenario.patients),
    reinforcements: {},
    triggeredInjects: [],
    drawings: byId(scenario.drawings),
    notes: [],
  };
}

const without = <T>(record: Record<string, T>, id: string): Record<string, T> => {
  const { [id]: _dropped, ...rest } = record;
  return rest;
};

export function applyEvent(state: RunState, event: Event): RunState {
  switch (event.type) {
    case 'SignPlaced': {
      const unit = state.units[event.signId];
      const label = event.label ?? unit?.callsign;
      const signs = {
        ...state.signs,
        [event.signId]: {
          id: event.signId,
          signType: event.signType,
          position: event.position,
          label,
          organisation: event.organisation ?? unit?.organisation,
        },
      };
      const units = unit
        ? { ...state.units, [unit.id]: { ...unit, position: event.position } }
        : state.units;
      return { ...state, signs, units };
    }
    case 'SignMoved': {
      const sign = state.signs[event.signId];
      if (!sign) return state;
      const signs = { ...state.signs, [sign.id]: { ...sign, position: event.position } };
      const unit = state.units[sign.id];
      const units = unit
        ? { ...state.units, [unit.id]: { ...unit, position: event.position } }
        : state.units;
      return { ...state, signs, units };
    }
    case 'SignRemoved': {
      const unit = state.units[event.signId];
      const units = unit
        ? { ...state.units, [unit.id]: { ...unit, position: undefined } }
        : state.units;
      return { ...state, signs: without(state.signs, event.signId), units };
    }
    case 'UnitStatusChanged': {
      const unit = state.units[event.unitId];
      if (!unit) return state;
      return { ...state, units: { ...state.units, [unit.id]: { ...unit, status: event.status } } };
    }
    case 'ReinforcementRequested':
      return {
        ...state,
        reinforcements: {
          ...state.reinforcements,
          [event.requestId]: {
            id: event.requestId,
            signType: event.signType,
            count: event.count,
            text: event.text,
            arrived: false,
          },
        },
      };
    case 'ReinforcementArrived': {
      const r = state.reinforcements[event.requestId];
      if (!r) return state;
      return {
        ...state,
        reinforcements: { ...state.reinforcements, [r.id]: { ...r, arrived: true } },
      };
    }
    case 'PatientTriaged': {
      const p = state.patients[event.patientId];
      if (!p) return state;
      return {
        ...state,
        patients: { ...state.patients, [p.id]: { ...p, category: event.category } },
      };
    }
    case 'PatientTransferred': {
      const p = state.patients[event.patientId];
      if (!p) return state;
      return {
        ...state,
        patients: { ...state.patients, [p.id]: { ...p, destination: event.destination } },
      };
    }
    case 'SurgicalIndicationSet': {
      const p = state.patients[event.patientId];
      if (!p) return state;
      return {
        ...state,
        patients: { ...state.patients, [p.id]: { ...p, surgicalIndication: event.indicated } },
      };
    }
    case 'TransportReleaseSet': {
      const p = state.patients[event.patientId];
      if (!p) return state;
      return {
        ...state,
        patients: { ...state.patients, [p.id]: { ...p, transportReleased: event.released } },
      };
    }
    case 'PatientAdded':
      return { ...state, patients: { ...state.patients, [event.patient.id]: event.patient } };
    case 'InjectTriggered':
      return { ...state, triggeredInjects: [...state.triggeredInjects, event.injectId] };
    case 'DrawingAdded':
      return { ...state, drawings: { ...state.drawings, [event.drawing.id]: event.drawing } };
    case 'DrawingRemoved':
      return { ...state, drawings: without(state.drawings, event.drawingId) };
    case 'NoteAdded':
      return {
        ...state,
        notes: [...state.notes, { id: event.id, time: event.time, text: event.text }],
      };
  }
}

export function reduce(scenario: Scenario, events: Event[]): RunState {
  return events.reduce(applyEvent, initialState(scenario));
}
